import { buildProfileContextForAnalysis, getProfileContextGate } from "./profileContinuity";

const VALID_4E_MODES = new Set(["ai", "jung", "freud"]);
const BACKEND_ANALYSIS_SOURCE = "backend_4e";
const LOCAL_MOCK_SOURCE = "local_mock";
const LANG_STORAGE_KEY = "morfeusz:lang";

const readCurrentLang = () => {
  if (typeof window === "undefined") return "pl";
  try {
    const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (stored === "pl" || stored === "en") return stored;
  } catch (_) {
    /* ignore */
  }
  const docLang = String(document?.documentElement?.lang || "").toLowerCase();
  return docLang.startsWith("en") ? "en" : "pl";
};

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).map((item) => String(item).trim()).filter(Boolean);
  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const normalizeText = (value) => String(value || "").replace(/\s+/g, " ").trim();

const normalizeMode = (mode) => (VALID_4E_MODES.has(mode) ? mode : "ai");

const normalizeDreamForFollowup = (dream) => ({
  id: String(dream?.id || ""),
  title: String(dream?.title || "Sen bez tytułu").trim() || "Sen bez tytułu",
  bodyText: normalizeText(dream?.bodyText || dream?.plainText || dream?.text || ""),
  date: String(dream?.date || ""),
  time: String(dream?.time || ""),
  emotions: normalizeArray(dream?.emotions),
  symbols: normalizeArray(dream?.symbols),
  saveMode: dream?.saveMode === "e2e" ? "e2e" : "plain",
});

const normalizeAnalysisForFollowup = (analysis) => ({
  source: analysis?.source || LOCAL_MOCK_SOURCE,
  mode: normalizeMode(analysis?.mode),
  model: String(analysis?.model || ""),
  html: String(analysis?.html || ""),
  plainText: normalizeText(analysis?.plainText || ""),
  createdAt: String(analysis?.createdAt || ""),
});

const summarizeProfileGate = (gate) => ({
  enabled: Boolean(gate?.enabled),
  reason: gate?.reason || "unknown",
  buildOptin: Boolean(gate?.buildOptin),
  aiUseOptin: Boolean(gate?.aiUseOptin),
  hasPack: Boolean(gate?.hasPack),
  version: gate?.version || null,
  updatedAt: gate?.updatedAt || "",
  lastRebuiltAt: gate?.lastRebuiltAt || "",
});

export const build4EFollowupPayload = async ({ dream, analysis, question, source = "morfeusz_front" } = {}) => {
  const normalizedDream = normalizeDreamForFollowup(dream);
  const normalizedAnalysis = normalizeAnalysisForFollowup(analysis);
  const lang = readCurrentLang();
  const profileGate = summarizeProfileGate(getProfileContextGate());
  const profileContext = await buildProfileContextForAnalysis();
  const profileEnabled = Boolean(profileContext?.enabled);

  return {
    dreamId: normalizedDream.id,
    question: normalizeText(question),
    lang,
    dream: normalizedDream,
    analysis: normalizedAnalysis,
    profile: profileEnabled
      ? {
          enabled: true,
          tldr: profileContext.tldr || "",
          motifs: profileContext.motifs || null,
          emotions: profileContext.emotions || null,
          timeline: profileContext.timeline || null,
          samples: profileContext.samples || null,
        }
      : {
          enabled: false,
          reason: profileContext?.reason || profileGate.reason || "disabled",
        },
    profileGate,
    profileUsedExpected: profileEnabled && normalizedAnalysis.source === BACKEND_ANALYSIS_SOURCE,
    client: {
      source,
      appVersion: "front-mock-v1",
      lang,
    },
  };
};

export const assert4EFollowupPayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return { ok: false, reason: "payload_missing" };
  }

  if (!payload.dreamId) {
    return { ok: false, reason: "dream_id_missing" };
  }

  if (!payload.question) {
    return { ok: false, reason: "question_missing" };
  }

  if (!payload.dream?.bodyText) {
    return { ok: false, reason: "dream_text_missing" };
  }

  if (!payload.analysis?.html && !payload.analysis?.plainText) {
    return { ok: false, reason: "analysis_missing" };
  }

  if (!VALID_4E_MODES.has(payload.analysis.mode)) {
    return { ok: false, reason: "invalid_analysis_mode" };
  }

  return { ok: true, reason: "ok" };
};
